import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UserGroup {
  id: number;
  name: string;
  description?: string;
  created_date?: string;
}

interface Permission {
  id: number;
  name: string;
  description?: string;
  category?: string;
}

interface UserPermissionsState {
  // User group data
  currentUserGroup: UserGroup | null;
  permissions: Permission[];
  
  // Loading states
  isLoading: boolean;
  error: string | null;
  lastUpdated: string | null;
  
  // Actions
  setUserGroup: (group: UserGroup) => void;
  setPermissions: (permissions: Permission[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearData: () => void;
  
  // Computed getters
  hasPermission: (permissionName: string) => boolean;
  getPermissionsByCategory: (category?: string) => Permission[];
}

export const useUserPermissionsStore = create<UserPermissionsState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentUserGroup: null,
      permissions: [],
      isLoading: false,
      error: null,
      lastUpdated: null,
      
      // Actions
      setUserGroup: (group: UserGroup) => 
        set({ 
          currentUserGroup: group, 
          lastUpdated: new Date().toISOString() 
        }),
        
      setPermissions: (permissions: Permission[]) => 
        set({ 
          permissions, 
          lastUpdated: new Date().toISOString() 
        }),
        
      setLoading: (isLoading: boolean) => 
        set({ isLoading }),
        
      setError: (error: string | null) => 
        set({ error }),
        
      clearData: () => 
        set({ 
          currentUserGroup: null, 
          permissions: [], 
          error: null, 
          lastUpdated: null 
        }),
      
      // Computed getters
      hasPermission: (permissionName: string) => {
        const { permissions } = get();
        return permissions.some(p => p.name === permissionName);
      },
      
      getPermissionsByCategory: (category?: string) => {
        const { permissions } = get();
        if (!category) return permissions;
        return permissions.filter(p => p.category === category);
      }
    }),
    {
      name: 'user-permissions-storage', // localStorage key
      partialize: (state) => ({
        currentUserGroup: state.currentUserGroup,
        permissions: state.permissions,
        lastUpdated: state.lastUpdated
      }) // Only persist these fields
    }
  )
);

// Helper hook for common permission checks
export const usePermissionCheck = () => {
  const hasPermission = useUserPermissionsStore(state => state.hasPermission);
  const permissions = useUserPermissionsStore(state => state.permissions);
  const currentUserGroup = useUserPermissionsStore(state => state.currentUserGroup);
  
  return {
    hasPermission,
    permissions,
    currentUserGroup,
    canView: (resource: string) => hasPermission(`view_${resource}`),
    canEdit: (resource: string) => hasPermission(`edit_${resource}`),
    canDelete: (resource: string) => hasPermission(`delete_${resource}`)
  };
};
